/**
 * TourScanLogTable — read-only detailed-DAR scan table.
 * Renders the auto-populated `tour_scan_log` from the backend:
 *   [{ tag_name, description, photo_url, captured_at, method, ... }]
 *
 * Used on Sentinel DAR page and Client portal report detail.
 */
import { resolveMediaUrl } from "../lib/api";
import MediaImage from "./MediaImage";

function resolveSrc(url) {
  return resolveMediaUrl(url);
}

function fmtTs(iso, tz) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, tz ? { timeZone: tz } : undefined);
  } catch {
    return String(iso);
  }
}

export default function TourScanLogTable({ rows, siteTimezone, testIdPrefix = "dar-scan-log" }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <div
        className="mt-2 border border-dashed border-[#27272A] bg-[#0A0A0A] px-4 py-6 text-center text-xs uppercase tracking-widest text-zinc-500"
        data-testid={`${testIdPrefix}-empty`}
      >
        No tour scans recorded on this shift yet.
      </div>
    );
  }
  return (
    <div className="mt-2 overflow-x-auto border border-[#27272A]" data-testid={`${testIdPrefix}-table`}>
      <table className="w-full text-xs font-mono">
        <thead className="bg-[#0A0A0A] text-zinc-400">
          <tr>
            <th className="text-left px-3 py-2 border-b border-[#27272A]">TAG</th>
            <th className="text-left px-3 py-2 border-b border-[#27272A]">DESCRIPTION</th>
            <th className="text-left px-3 py-2 border-b border-[#27272A]">PHOTO</th>
            <th className="text-left px-3 py-2 border-b border-[#27272A]">TIMESTAMP</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const key = r.visit_id || `${r.stop_id || "scan"}-${i}`;
            const src = resolveSrc(r.photo_url);
            return (
              <tr key={key} className="align-top" data-testid={`${testIdPrefix}-row-${i}`}>
                <td className="px-3 py-2 border-b border-[#27272A] text-zinc-100">{r.tag_name || "—"}</td>
                <td className="px-3 py-2 border-b border-[#27272A] text-zinc-300 max-w-[280px]">{r.description || r.tag_name || "—"}</td>
                <td className="px-3 py-2 border-b border-[#27272A]">
                  {src ? (
                    <a
                      href={src}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-24"
                      data-testid={`${testIdPrefix}-photo-${i}`}
                    >
                      <MediaImage
                        src={src}
                        alt={r.description || r.tag_name || `scan ${i + 1}`}
                        className="w-24 h-16 object-cover border border-[#27272A]"
                        testId={`${testIdPrefix}-photo-img-${i}`}
                      />
                    </a>
                  ) : (
                    <span
                      className="text-zinc-500 italic"
                      data-testid={`${testIdPrefix}-photo-missing-${i}`}
                    >
                      no photo
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 border-b border-[#27272A] text-zinc-400">{fmtTs(r.captured_at, siteTimezone)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
